import { useCallback, useContext, useEffect, useRef, useState } from "react";

import api from "../../services/api";
import { SocketContext } from "../../context/Socket/SocketContext";

// Cada consulta renova a assinatura de presença e, nas conexões com a opção
// ligada, mantém o número online se o atendente está usando o CRM (o backend
// volta a offline após 90 s sem renovação — ContactPresenceService).
const REFRESH_MS = 60 * 1000;
// "digitando..." some sozinho se o "paused" do WhatsApp se perder.
const TYPING_TIMEOUT_MS = 25 * 1000;

const EMPTY_STATUS = { supported: false, presence: null, about: null, stories: [] };

// Sinal de vida da tela de atendimentos: mantém o número online (backend volta
// a offline após 90 s sem sinal) para o WhatsApp entregar o "digitando".
const HEARTBEAT_MS = 60 * 1000;

// Uso ativo do CRM, como o WhatsApp Web: aba visível, janela em foco e
// mouse/teclado nos últimos 5 min. Só assim o número fica online; quando a aba
// sai (perde o foco, fica parada, fecha) o backend é avisado na hora e o
// celular volta a notificar. 5 min e não 1: quem lê a conversa ou espera o
// cliente responder fica parado, e offline o WhatsApp não entrega o
// "digitando". Mensagem sem leitura nesse meio tempo o backend já avisa no
// celular depois de 3 min (ContactPresenceService).
const IDLE_MS = 5 * 60 * 1000;
const ACTIVITY_CHECK_MS = 5 * 1000;
// mousemove/keydown disparam sem parar: basta marcar a atividade a cada 5 s.
const INPUT_THROTTLE_MS = 5 * 1000;
const INPUT_EVENTS = ["mousedown", "mousemove", "keydown", "wheel", "touchstart"];
// Trocar de janela por um instante (alt-tab, clique em outro programa) não
// derruba o online: só sai se não voltar em 10 s. Aba oculta, fechada ou
// parada continuam saindo na hora.
const BLUR_GRACE_MS = 10 * 1000;
// Sinal de entrada que falhou: tenta de novo, sem martelar o backend.
const ENTER_RETRY_MS = 15 * 1000;

// Cada aba conta separado no backend: duas abas do mesmo atendente não se
// derrubam.
const TAB_ID = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
let lastInputAt = Date.now();
let blurredAt = 0;

const hasFocusOrGrace = () =>
	document.hasFocus() || (blurredAt > 0 && Date.now() - blurredAt < BLUR_GRACE_MS);

const isUserActive = () =>
	document.visibilityState === "visible" &&
	hasFocusOrGrace() &&
	Date.now() - lastInputAt < IDLE_MS;

const inactiveReason = () => {
	if (document.visibilityState !== "visible") return "hidden";
	if (!hasFocusOrGrace()) return "blur";
	return "idle";
};

// Aba fechando: o axios não termina a requisição, o fetch com keepalive sim.
// Se nem isso sair, os 90 s do backend cobrem.
const sendLeaveOnPageHide = () => {
	let authorization = api.defaults.headers.Authorization;
	try {
		const token = localStorage.getItem("token");
		if (token) authorization = `Bearer ${JSON.parse(token)}`;
	} catch (err) {
		// Token ilegível: fica o do axios.
	}
	try {
		fetch(`${api.defaults.baseURL}/ticket/online-heartbeat`, {
			method: "POST",
			keepalive: true,
			credentials: "include",
			headers: {
				"Content-Type": "application/json",
				...(authorization ? { Authorization: authorization } : {}),
			},
			body: JSON.stringify({ active: false, tabId: TAB_ID, reason: "pagehide" }),
		}).catch(() => {});
	} catch (err) {
		// Navegador sem keepalive: o prazo do backend resolve.
	}
};

// Quem está digitando/gravando agora. Quem alimenta é a tela de atendimentos
// (useAttendanceWhatsappPresence, um listener só); o card da lista lê daqui
// com useContactTyping, sem abrir um listener de socket por card.
const typingByContact = new Map();
const typingListeners = new Map();

const publishContactTyping = (contactId, presence) => {
	if (!contactId) return;
	const next = presence === "composing" || presence === "recording" ? presence : null;
	if ((typingByContact.get(contactId) || null) === next) return;
	if (next) {
		typingByContact.set(contactId, next);
	} else {
		typingByContact.delete(contactId);
	}
	(typingListeners.get(contactId) || new Set()).forEach(listener => listener(next));
};

/** "composing" | "recording" | null do contato, para o card da lista. */
export const useContactTyping = contactId => {
	const [typing, setTyping] = useState(() => typingByContact.get(contactId) || null);

	useEffect(() => {
		if (!contactId) return undefined;
		setTyping(typingByContact.get(contactId) || null);
		const listeners = typingListeners.get(contactId) || new Set();
		listeners.add(setTyping);
		typingListeners.set(contactId, listeners);
		return () => {
			listeners.delete(setTyping);
			if (listeners.size === 0) typingListeners.delete(contactId);
		};
	}, [contactId]);

	return typing;
};

/**
 * Tela de atendimentos aberta: mantém o número online enquanto o atendente
 * está usando o CRM (como o WhatsApp Web) e recebe o "digitando" de todos os
 * contatos para os cards da lista.
 */
export const useAttendanceWhatsappPresence = () => {
	const socketManager = useContext(SocketContext);

	useEffect(() => {
		let wasActive = false;
		let enterRetryAt = 0;

		const send = (active, reason) => {
			api.post("/ticket/online-heartbeat", { active, tabId: TAB_ID, reason }).catch(() => {
				// Sem o sinal de entrada o número fica offline: a aba não pode
				// achar que está online, senão só tentaria de novo no próximo
				// minuto.
				if (active && wasActive) {
					wasActive = false;
					enterRetryAt = Date.now() + ENTER_RETRY_MS;
				}
			});
		};

		const leave = reason => {
			if (!wasActive) return;
			wasActive = false;
			send(false, reason);
		};

		// Entrou em uso: sinal na hora. Parou: avisa a saída na hora.
		const sync = () => {
			if (!isUserActive()) {
				leave(inactiveReason());
				return;
			}
			if (wasActive || Date.now() < enterRetryAt) return;
			wasActive = true;
			send(true);
		};

		const onInput = () => {
			const now = Date.now();
			if (now - lastInputAt < INPUT_THROTTLE_MS) return;
			lastInputAt = now;
			sync();
		};
		const onFocus = () => {
			blurredAt = 0;
			lastInputAt = Date.now();
			sync();
		};
		// A saída por troca de janela fica para a checagem periódica, depois da
		// carência.
		const onBlur = () => {
			blurredAt = Date.now();
		};
		const onVisibilityChange = () => {
			if (document.visibilityState === "visible") {
				onFocus();
			} else {
				leave("hidden");
			}
		};
		const onPageHide = () => {
			if (!wasActive) return;
			wasActive = false;
			sendLeaveOnPageHide();
		};

		sync();
		const heartbeat = setInterval(() => {
			if (isUserActive() && wasActive) {
				send(true);
			} else {
				sync();
			}
		}, HEARTBEAT_MS);
		const idleCheck = setInterval(sync, ACTIVITY_CHECK_MS);

		INPUT_EVENTS.forEach(event =>
			window.addEventListener(event, onInput, { capture: true, passive: true })
		);
		window.addEventListener("focus", onFocus);
		window.addEventListener("blur", onBlur);
		window.addEventListener("pagehide", onPageHide);
		document.addEventListener("visibilitychange", onVisibilityChange);

		return () => {
			clearInterval(heartbeat);
			clearInterval(idleCheck);
			INPUT_EVENTS.forEach(event =>
				window.removeEventListener(event, onInput, { capture: true, passive: true })
			);
			window.removeEventListener("focus", onFocus);
			window.removeEventListener("blur", onBlur);
			window.removeEventListener("pagehide", onPageHide);
			document.removeEventListener("visibilitychange", onVisibilityChange);
			leave("unmount");
		};
	}, []);

	useEffect(() => {
		const companyId = localStorage.getItem("companyId");
		const socket = socketManager.getSocket(companyId);
		const timers = new Map();

		const onTyping = data => {
			if (!data?.contactId) return;
			clearTimeout(timers.get(data.contactId));
			timers.delete(data.contactId);
			publishContactTyping(data.contactId, data.typing);
			if (data.typing) {
				timers.set(
					data.contactId,
					setTimeout(() => {
						timers.delete(data.contactId);
						publishContactTyping(data.contactId, null);
					}, TYPING_TIMEOUT_MS)
				);
			}
		};

		socket.on(`company-${companyId}-contactTyping`, onTyping);

		return () => {
			timers.forEach(timer => clearTimeout(timer));
			socket.disconnect();
		};
	}, [socketManager]);
};

/**
 * Presença, recado e status de 24h do contato do ticket, como o WhatsApp
 * mostra. Só conexões QR têm isso; nas demais `supported` vem false.
 */
const useContactWhatsappStatus = (ticketId, contactId) => {
	const socketManager = useContext(SocketContext);
	const [status, setStatus] = useState(EMPTY_STATUS);
	const typingTimerRef = useRef(null);

	const clearTypingTimer = useCallback(() => {
		if (typingTimerRef.current) {
			clearTimeout(typingTimerRef.current);
			typingTimerRef.current = null;
		}
	}, []);

	// Vale para o socket e para a leitura periódica: sem isto, um "digitando"
	// trazido pela leitura ficava na tela até a próxima.
	const expireTypingLater = useCallback(() => {
		clearTypingTimer();
		typingTimerRef.current = setTimeout(() => {
			typingTimerRef.current = null;
			setStatus(prev =>
				prev.presence && ["composing", "recording"].includes(prev.presence.presence)
					? { ...prev, presence: { ...prev.presence, presence: "available" } }
					: prev
			);
		}, TYPING_TIMEOUT_MS);
	}, [clearTypingTimer]);

	const fetchStatus = useCallback(async () => {
		if (!ticketId) return;
		try {
			const { data } = await api.get(`/tickets/${ticketId}/contact-status`, {
				params: { active: isUserActive() ? 1 : 0, tabId: TAB_ID },
			});
			if (data?.contactId !== contactId) return;
			setStatus({ ...EMPTY_STATUS, ...data });
			if (["composing", "recording"].includes(data.presence?.presence)) {
				expireTypingLater();
			}
		} catch (err) {
			// Silencioso: é um complemento do cabeçalho, não pode atrapalhar o ticket.
		}
	}, [ticketId, contactId, expireTypingLater]);

	useEffect(() => {
		setStatus(EMPTY_STATUS);
		if (!ticketId || !contactId) return undefined;

		fetchStatus();
		// A resposta da assinatura de presença pode chegar antes de o ticket
		// entrar na sala do socket; a segunda leitura pega o que o backend guardou.
		const followUp = setTimeout(fetchStatus, 4000);
		const isVisible = () => document.visibilityState === "visible";
		const timer = setInterval(() => {
			if (isVisible()) fetchStatus();
		}, REFRESH_MS);
		const onVisibilityChange = () => {
			if (isVisible()) fetchStatus();
		};
		document.addEventListener("visibilitychange", onVisibilityChange);
		return () => {
			clearTimeout(followUp);
			clearInterval(timer);
			document.removeEventListener("visibilitychange", onVisibilityChange);
		};
	}, [ticketId, contactId, fetchStatus]);

	useEffect(() => {
		if (!ticketId || !contactId) return undefined;

		const companyId = localStorage.getItem("companyId");
		const socket = socketManager.getSocket(companyId);

		const onPresence = data => {
			if (!data || data.contactId !== contactId) return;
			clearTypingTimer();
			setStatus(prev => ({
				...prev,
				presence: {
					contactId,
					presence: data.presence,
					lastSeen: data.lastSeen ?? null,
					updatedAt: data.updatedAt ?? Date.now(),
				},
			}));

			if (data.presence === "composing" || data.presence === "recording") {
				expireTypingLater();
			}
		};

		const onStories = data => {
			if (!data || data.contactId !== contactId) return;
			fetchStatus();
		};

		socket.on(`company-${companyId}-contactPresence`, onPresence);
		socket.on(`company-${companyId}-contactStories`, onStories);

		return () => {
			clearTypingTimer();
			socket.disconnect();
		};
	}, [ticketId, contactId, socketManager, fetchStatus, clearTypingTimer, expireTypingLater]);

	return status;
};

export default useContactWhatsappStatus;
